import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { home } from "@faire/retailer-visitor-shared/paths/index";
import { RetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { UserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { useMakerSignUp } from "@faire/retailer-visitor-shared/views/apply/MakerApplication/useMakerSignUp";
import { CTAPosition, StyledSellOnFaireButton } from "@faire/retailer-visitor-shared/views/brands/landing-page/const";
import { useBrandsLandingPageState } from "@faire/retailer-visitor-shared/views/brands/landing-page/hooks/useBrandsLandingPageState";
import { getIsoToCountryNames } from "@faire/web--source/common/consts/ISO_TO_COUNTRY_NAME";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { Button } from "@faire/web--source/slate/Button";
import { Opacity } from "@faire/web--source/slate/Color";
import { Input } from "@faire/web--source/slate/Input";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackBrandLeadsApplyToSellOnFaireClick } from "@faire/web-api--source/events/brandLeads/click/applyToSellOnFaire";
import { trackBrandLeadsBrandsPageSignInClick } from "@faire/web-api--source/events/brandLeads/click/brandsPageSignIn";
import { trackBrandLeadsScheduleACallClick } from "@faire/web-api--source/events/brandLeads/click/scheduleACall";
import { trackBrandLeadsBrandsPageHeaderSectionView } from "@faire/web-api--source/events/brandLeads/view/brandsPageHeaderSection";
import apply from "@faire/web-api--source/routes/www/apply";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import styled from "styled-components";
var ReferrerInfoBase = function() {
    var _useMakerSignUp = useMakerSignUp(), referrerInformation = _useMakerSignUp.referrerInformation, otherReferringRetailerCount = _useMakerSignUp.otherReferringRetailerCount, isLoading = _useMakerSignUp.isLoading, isM2M = _useMakerSignUp.isM2M;
    if (!(referrerInformation === null || referrerInformation === void 0 ? void 0 : referrerInformation.name) && !otherReferringRetailerCount || isLoading) {
        return null;
    }
    if (referrerInformation === null || referrerInformation === void 0 ? void 0 : referrerInformation.name) {
        if (isM2M || !otherReferringRetailerCount) {
            return /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "fDUXN8",
                defaultMessage: [
                    {
                        type: 1,
                        value: "referrerName"
                    },
                    {
                        type: 0,
                        value: " has invited you"
                    }
                ],
                values: DEPRECATED_backwardsCompatibleValues({
                    referrerName: referrerInformation.name
                })
            });
        }
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "4rk06C",
            defaultMessage: [
                {
                    type: 1,
                    value: "referrerName"
                },
                {
                    type: 0,
                    value: " + "
                },
                {
                    type: 1,
                    value: "referrerCount"
                },
                {
                    type: 0,
                    value: " more"
                }
            ],
            values: DEPRECATED_backwardsCompatibleValues({
                referrerName: referrerInformation.name,
                referrerCount: otherReferringRetailerCount
            })
        });
    }
    return /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "oBunVA",
        defaultMessage: [
            {
                type: 6,
                value: "referrerCount",
                options: {
                    one: {
                        value: [
                            {
                                type: 7
                            },
                            {
                                type: 0,
                                value: " more has invited you"
                            }
                        ]
                    },
                    other: {
                        value: [
                            {
                                type: 7
                            },
                            {
                                type: 0,
                                value: " more have invited you"
                            }
                        ]
                    }
                },
                offset: 0,
                pluralType: "cardinal"
            }
        ],
        values: DEPRECATED_backwardsCompatibleValues({
            referrerCount: otherReferringRetailerCount !== null && otherReferringRetailerCount !== void 0 ? otherReferringRetailerCount : 1
        })
    });
};
var ReferrerInfo = observer(ReferrerInfoBase);
var BrandsLandingPageTopSectionBase = function(props) {
    useEffect(function() {
        trackBrandLeadsBrandsPageHeaderSectionView();
    }, []);
    return /*#__PURE__*/ _jsxs(Column, {
        style: {
            backgroundColor: Decorative.shade.yellow,
            width: "100%"
        },
        children: [
            /*#__PURE__*/ _jsx(SellOnFaire, {}),
            /*#__PURE__*/ _jsx(HeaderContent, {}),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "4x",
                ref: props.categoryInclusiveReference
            }),
            /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                desktopAndAbove: /*#__PURE__*/ _jsx(Spacer, {
                    height: "7x"
                })
            })
        ]
    });
};
export var BrandsLandingPageTopSection = observer(BrandsLandingPageTopSectionBase);
var getCountrySpecificHeaderMessage = function(isoCountryCode, strictLocalize) {
    switch(isoCountryCode){
        case "FRA":
            return strictLocalize({
                id: "joAwpe",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Connect with independent retailers from the U.S., France, and beyond"
                    }
                ]
            });
        case "DEU":
            return strictLocalize({
                id: "MVVAwy",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Connect with independent retailers from the U.S., Germany, and beyond"
                    }
                ]
            });
        case "ESP":
            return strictLocalize({
                id: "AJsV0s",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Connect with independent retailers from the U.S., Spain, and beyond"
                    }
                ]
            });
        case "ITA":
            return strictLocalize({
                id: "xUPx8x",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Connect with independent retailers from the U.S., Italy, and beyond"
                    }
                ]
            });
        default:
            return strictLocalize({
                id: "Nu3CvN",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Connect with independent retailers from the U.S., "
                    },
                    {
                        type: 1,
                        value: "FromCountry"
                    },
                    {
                        type: 0,
                        value: ", and beyond"
                    }
                ]
            }, {
                FromCountry: // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
                getIsoToCountryNames(strictLocalize)[isoCountryCode]
            });
    }
};
var getGetStartedMessage = function(isEurope, strictLocalize) {
    return isEurope ? strictLocalize({
        id: "Cfgq09",
        defaultMessage: [
            {
                type: 0,
                value: "Get Started"
            }
        ]
    }) : strictLocalize({
        id: "rvfNE3",
        defaultMessage: [
            {
                type: 0,
                value: "Start Selling"
            }
        ]
    });
};
var MobileTitle = function() {
    var isEurope = useNationality().isEurope;
    return /*#__PURE__*/ _jsxs(Column, {
        align: "flex-start",
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: {
                    mobileAndAbove: "displaySSerifRegular",
                    tabletAndAbove: "displayMSerifRegular"
                },
                color: Core.text.inverse,
                children: isEurope ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "WDPp0J",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Sell to hundreds of thousands of retailers across Europe and the U.S."
                        }
                    ]
                }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "2MDUV+",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Access hundreds of thousands of retailers around the world"
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            }),
            /*#__PURE__*/ _jsx(BrandsPageSubTitle, {})
        ]
    });
};
var BrandsDesktopTitle = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    return /*#__PURE__*/ _jsxs(Column, {
        align: "center",
        children: [
            /*#__PURE__*/ _jsx(DesktopTitle, {
                variant: "displayLSerifRegular",
                color: Core.text.inverse,
                align: "center",
                children: RetailerStore.get().isEurope ? getCountrySpecificHeaderMessage(RetailerStore.get().retailerCountry, strictLocalize) : /*#__PURE__*/ _jsx(_Fragment, {
                    children: RetailerStore.get().isCanadian ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "IfnXWm",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Reach independent retailers across Canada, the US, Europe, and more"
                            }
                        ]
                    }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "2ExGzU",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Reach independent retailers across the US, Canada, Europe, and more"
                            }
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            }),
            /*#__PURE__*/ _jsx(BrandsPageSubTitle, {})
        ]
    });
};
var BrandsPageTitle = function() {
    return /*#__PURE__*/ _jsx(ResponsiveContentV2, {
        tabletAndBelow: /*#__PURE__*/ _jsx(MobileTitle, {}),
        desktopAndAbove: /*#__PURE__*/ _jsx(BrandsDesktopTitle, {})
    });
};
var BrandsPageSubTitle = function() {
    return /*#__PURE__*/ _jsx(ResponsiveContentV2, {
        tabletAndBelow: /*#__PURE__*/ _jsx(Typography, {
            variant: "paragraphSansRegular",
            align: "left",
            color: Core.text.inverse,
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "Yy+pxH",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Sign up to start selling in Faire\u2019s B2B marketplace. No set-up fees. No commitments."
                    }
                ]
            })
        }),
        desktopAndAbove: /*#__PURE__*/ _jsx(Typography, {
            variant: "subheadingSansRegular",
            align: "center",
            color: Core.text.inverse,
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "Yy+pxH",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Sign up to start selling in Faire\u2019s B2B marketplace. No set-up fees. No commitments."
                    }
                ]
            })
        })
    });
};
var SellOnFaireBase = function() {
    var isR2BorB2B = useBrandsLandingPageState().isR2BorB2B;
    var _useMakerSignUp = useMakerSignUp(), referrerInformation = _useMakerSignUp.referrerInformation, otherReferringRetailerCount = _useMakerSignUp.otherReferringRetailerCount, isLoading = _useMakerSignUp.isLoading;
    var referrerExists = ((referrerInformation === null || referrerInformation === void 0 ? void 0 : referrerInformation.name) || otherReferringRetailerCount) && !isLoading;
    return /*#__PURE__*/ _jsx(ResponsiveContentV2, {
        tabletAndBelow: /*#__PURE__*/ _jsx(Spacer, {
            height: "1x"
        }),
        desktopAndAbove: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "5x"
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "labelSansRegular",
                    color: Core.text.inverse,
                    align: "center",
                    "data-test-id": "referrer-info",
                    as: "h1",
                    children: isR2BorB2B && referrerExists ? /*#__PURE__*/ _jsx(ReferrerInfo, {}) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "1X6kNJ",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Sell on Faire"
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "3x"
                })
            ]
        })
    });
};
var SellOnFaire = observer(SellOnFaireBase);
var SignInLink = function(chunk) {
    var signInUrl = UserStore.get().userToken ? home() : home({
        signIn: "1"
    });
    return /*#__PURE__*/ _jsx(EmbeddedLink, {
        color: Core.text.inverse,
        onClick: function() {
            return trackBrandLeadsBrandsPageSignInClick(CTAPosition.TOP_SECTION);
        },
        linkTo: signInUrl,
        children: chunk
    });
};
var HeaderContentBase = function() {
    var isPersonalized = useBrandsLandingPageState().isPersonalized;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(BrandsPageTitleWrapper, {
                children: /*#__PURE__*/ _jsx(BrandsPageTitle, {})
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: {
                    mobileAndAbove: "3x",
                    tabletAndAbove: "7x"
                }
            }),
            isPersonalized ? /*#__PURE__*/ _jsx(PersonalizedHeaderContent, {}) : /*#__PURE__*/ _jsx(UnpersonalizedHeaderContent, {}),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            }),
            /*#__PURE__*/ _jsx(AlreadyOnFaireWrapper, {
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansRegular",
                    color: Core.text.inverse,
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "tvVeBi",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Already on Faire? "
                            },
                            {
                                type: 8,
                                value: "SignInLink",
                                children: [
                                    {
                                        type: 0,
                                        value: "Sign In"
                                    }
                                ]
                            }
                        ],
                        values: DEPRECATED_backwardsCompatibleValues({
                            SignInLink: SignInLink
                        })
                    })
                })
            })
        ]
    });
};
var HeaderContent = observer(HeaderContentBase);
var MobileUnpersonalizedHeaderContentBase = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useBrandsLandingPageState = useBrandsLandingPageState(), emailAddress = _useBrandsLandingPageState.emailAddress, handleEmailChange = _useBrandsLandingPageState.handleEmailChange, goToForm = _useBrandsLandingPageState.goToForm;
    return /*#__PURE__*/ _jsx(Column, {
        style: {
            width: "100%",
            padding: getSpacing(0, "3x")
        },
        align: "flex-start",
        justify: "left",
        children: /*#__PURE__*/ _jsxs(ContainerForm, {
            action: apply(),
            onSubmit: goToForm,
            children: [
                /*#__PURE__*/ _jsx(TopSectionEmailInputField, {
                    type: "text",
                    name: "email_address",
                    placeholder: strictLocalize({
                        id: "NAzQvU",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Email address"
                            }
                        ]
                    }),
                    autoComplete: "email",
                    value: emailAddress,
                    onInput: handleEmailChange,
                    "data-test-id": "top-section-email-input-mobile"
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "2x"
                }),
                /*#__PURE__*/ _jsx(TopSectionApplyButton, {
                    onClick: function() {
                        trackBrandLeadsApplyToSellOnFaireClick(CTAPosition.TOP_SECTION);
                    },
                    "data-test-id": "top-section-get-started-mobile-button",
                    type: "submit",
                    children: getGetStartedMessage(RetailerStore.get().isEurope, strictLocalize)
                })
            ]
        })
    });
};
var MobileUnpersonalizedHeaderContent = observer(MobileUnpersonalizedHeaderContentBase);
var DesktopUnpersonalizedHeaderContentBase = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useBrandsLandingPageState = useBrandsLandingPageState(), emailAddress = _useBrandsLandingPageState.emailAddress, handleEmailChange = _useBrandsLandingPageState.handleEmailChange, goToForm = _useBrandsLandingPageState.goToForm;
    return /*#__PURE__*/ _jsxs(ContainerForm, {
        action: apply(),
        onSubmit: goToForm,
        children: [
            /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsx(TopSectionEmailInputField, {
                    type: "text",
                    name: "email_address",
                    placeholder: strictLocalize({
                        id: "NAzQvU",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Email address"
                            }
                        ]
                    }),
                    autoComplete: "email",
                    value: emailAddress,
                    onChange: handleEmailChange,
                    "data-test-id": "top-section-email-input-desktop"
                })
            }),
            /*#__PURE__*/ _jsx(TopSectionApplyButton, {
                onClick: function() {
                    trackBrandLeadsApplyToSellOnFaireClick(CTAPosition.TOP_SECTION);
                },
                "data-test-id": "top-section-get-started-desktop-button",
                type: "submit",
                children: getGetStartedMessage(RetailerStore.get().isEurope, strictLocalize)
            })
        ]
    });
};
var DesktopUnpersonalizedHeaderContent = observer(DesktopUnpersonalizedHeaderContentBase);
var UnpersonalizedHeaderContentBase = function() {
    return /*#__PURE__*/ _jsx(ResponsiveContentV2, {
        mobile: /*#__PURE__*/ _jsx(MobileUnpersonalizedHeaderContent, {}),
        tabletAndAbove: /*#__PURE__*/ _jsx(DesktopUnpersonalizedHeaderContent, {})
    });
};
var UnpersonalizedHeaderContent = observer(UnpersonalizedHeaderContentBase);
var PersonalizedHeaderContent = function() {
    var calendlyLink = useBrandsLandingPageState().calendlyLink;
    return /*#__PURE__*/ _jsx(Row, {
        style: {
            width: "100%"
        },
        justify: "center",
        children: /*#__PURE__*/ _jsx(StyledSellOnFaireButton, {
            linkTo: calendlyLink,
            target: "_blank",
            "data-test-id": "top-section-schedule-call-button",
            onClick: function() {
                trackBrandLeadsScheduleACallClick(CTAPosition.TOP_SECTION);
            },
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "ohZEk7",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Schedule a Call"
                    }
                ]
            })
        })
    });
};
var AlreadyOnFaireWrapper = styled(Column).attrs({
    justify: {
        mobileAndAbove: "left",
        tabletAndAbove: "center"
    },
    align: {
        mobileAndAbove: "flex-start",
        tabletAndAbove: "center"
    }
}).withConfig({
    componentId: "sc-314bcf16-0"
})([
    "padding:",
    ";width:100%;"
], getSpacing(0, "3x"));
var DesktopTitle = styled(Typography).withConfig({
    componentId: "sc-314bcf16-1"
})([
    "max-width:780px;"
]);
var TopSectionApplyButton = styled(Button).attrs({
    variant: "secondary"
}).withConfig({
    componentId: "sc-314bcf16-2"
})([
    "width:max-content;height:50px;color:",
    ";background-color:",
    ";border:1.2px solid ",
    ";",
    "{width:100%;justify-content:center;align-items:center;padding:0;}"
], Core.text.default, Core.surface.inverse, Core.border.muted, responsiveMediaQueries.mobile);
var BrandsPageTitleWrapper = styled(Column).attrs({
    justify: {
        mobileAndAbove: "left",
        tabletAndAbove: "center"
    },
    align: {
        mobileAndAbove: "flex-start",
        tabletAndAbove: "center"
    }
}).withConfig({
    componentId: "sc-314bcf16-3"
})([
    "width:100%;padding:",
    ";"
], getSpacing(0, "3x"));
var ContainerForm = styled.form.withConfig({
    componentId: "sc-314bcf16-4"
})([
    "width:100%;justify-content:center;display:flex;flex-wrap:wrap;flex-direction:row;align-content:center;",
    "{flex-direction:column;justify-content:flex-start;align-items:flex-start;}"
], responsiveMediaQueries.mobile);
var TopSectionEmailInputField = styled(Input).withConfig({
    componentId: "sc-314bcf16-5"
})([
    "width:280px;height:50px;border:1.2px solid ",
    ";background:none;input{color:",
    ";&::placeholder{color:",
    ";}}&:focus,&:focus-within{border-color:",
    ";input{&::placeholder{color:",
    ";}}}",
    "{height:40px;width:100%;}"
], Core.border.muted, Core.text.inverse, Core.text.inverse, Core.border.muted, Core.text.inverse + Opacity.O_50, responsiveMediaQueries.mobile);
